import { Status } from '../constants';
import type {
  Exercise,
  ExerciseGroup,
  Member,
  Muscle,
  PerformedExerciseGroup,
  PerformedRoutine,
  PerformedSet,
  TrainingRoutine,
  TrainingRoutineSession,
  InitialPreferences,
  SignUpPreferences,
  SetGroup,
  PerformedCircuit,
  PerformedSetGroup,
} from '../types';

export const isActiveSet = (set: PerformedSet): boolean =>
  set.status === Status.Active;

export const isPendingSet = (set: PerformedSet): boolean =>
  set.status === Status.Pending;

export const isPendingSets = (sets: PerformedSetGroup): boolean =>
  sets.every(isPendingSet);

export const isCompleteSet = (set: PerformedSet): boolean =>
  set.status === Status.Complete;

export const isCompleteGroupSet = (
  performedSetGroup: PerformedSetGroup,
): boolean => performedSetGroup.every(isCompleteSet);

export const isCompleteTrainingRoutine = (
  trainingRoutineSession: TrainingRoutineSession,
): boolean => {
  return trainingRoutineSession.status === Status.Complete;
};

export const isGroupSetActive = (
  performedSetGroup: PerformedSetGroup,
): boolean => {
  return performedSetGroup.some(isActiveSet);
};

export const hasPendingSet = (sets: PerformedSetGroup): boolean => {
  return sets.some(isPendingSet);
};

export const hasPendingSetInGroup = (
  performedCircuit: PerformedCircuit,
): boolean => {
  return performedCircuit.some(hasPendingSet);
};

export const startExerciseGroup = ({ sets }: PerformedExerciseGroup): void => {
  const firstPendingGroupSet = sets.find((setGroup) =>
    setGroup.some(isPendingSet),
  );
  if (!firstPendingGroupSet) {
    return;
  }
  const firstPendingSet = firstPendingGroupSet.find(isPendingSet);
  if (!firstPendingSet) {
    return;
  }
  firstPendingSet.status = Status.Active;
  firstPendingSet.startedTimestamp = Date.now();
};

export const getAllSets = (
  performedExerciseGroups: PerformedExerciseGroup[],
): PerformedSetGroup => {
  return performedExerciseGroups
    .map(({ sets }) => sets)
    .flat()
    .flat();
};

export const getAllTrainingSets = (
  exerciseGroups: ExerciseGroup[],
): SetGroup => {
  return exerciseGroups
    .map(({ sets }) => sets)
    .flat()
    .flat();
};

export const getUniqueExerciseIdsListFromRoutine = (
  routine: TrainingRoutine | PerformedRoutine,
): string[] => {
  const exerciseGroups =
    (routine as TrainingRoutine).exerciseGroups ||
    (routine as PerformedRoutine).performedExerciseGroups;
  return [
    ...new Set(
      exerciseGroups
        .map((exerciseGroup) =>
          exerciseGroup.sets
            .map((performedCircuit) =>
              performedCircuit.map(({ exerciseId }) => exerciseId),
            )
            .flat(),
        )
        .flat(),
    ),
  ];
};

export const isNumeric = (value: any): value is number =>
  typeof value === 'number' && !isNaN(value);

export const capitalizeString = (input: string): string => {
  if (!input) return input;
  return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
};

const PIN_DEFAULT_LENGTH = 4;
const PIN_MIN_LENGTH = 4;
const PIN_MAX_LENGTH = 5;
const PIN_RANDOM_ATTEMPTS = 25;

type GenerateNewPinOptions = {
  pinLength?: number;
};

const clampPinLength = (requestedLength?: number): number => {
  if (!requestedLength) {
    return PIN_DEFAULT_LENGTH;
  }

  const normalizedLength = Math.floor(requestedLength);
  if (Number.isNaN(normalizedLength)) {
    return PIN_DEFAULT_LENGTH;
  }

  return Math.max(PIN_MIN_LENGTH, Math.min(PIN_MAX_LENGTH, normalizedLength));
};

const getPinRange = (pinLength: number): { min: number; max: number } => {
  const min = Math.pow(10, pinLength - 1);
  const max = Math.pow(10, pinLength) - 1;

  return { min, max };
};

export const generateNewPin = (
  members: Member[],
  options: GenerateNewPinOptions = {},
): string => {
  const pinLength = clampPinLength(options.pinLength);
  const { min, max } = getPinRange(pinLength);
  const rangeSize = max - min + 1;
  const existingPins = new Set(members.map(({ pin }) => pin));

  for (
    let attempt = 0;
    attempt < Math.min(rangeSize, PIN_RANDOM_ATTEMPTS);
    attempt++
  ) {
    const candidate = Math.floor(Math.random() * rangeSize) + min;
    const pinCandidate = candidate.toString();

    if (!existingPins.has(pinCandidate)) {
      return pinCandidate;
    }
  }

  for (let candidate = min; candidate <= max; candidate++) {
    const pinCandidate = candidate.toString();
    if (!existingPins.has(pinCandidate)) {
      return pinCandidate;
    }
  }

  throw new Error(
    `Unable to generate a unique ${pinLength}-digit pin. All combinations appear to be in use.`,
  );
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return emailRegex.test(email);
};

export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  const regex = /^(?:\+\d{1,3})?[0-9]\d{9}$/;
  return regex.test(phoneNumber.replace(/[\s-]/g, ''));
};

export const memberAllowedToSendEmail = (member: Member): boolean => {
  return !member.preferences?.unsubscribeFromAllEmails;
};

type NewMemberRequiredValues = {
  firstName: Member['firstName'];
  lastName: Member['lastName'];
  pin: Member['pin'];
  email: Member['email'];
  phoneNumber: Member['phoneNumber'];
};

export const getNewMember = (
  memberOverrides: NewMemberRequiredValues &
    Partial<Omit<Member, keyof NewMemberRequiredValues>>,
  signUpPreferences: SignUpPreferences = {},
  initialPreferences: InitialPreferences = {},
): Member => {
  return {
    id: '',
    dateOfBirth: '',
    signedDate: new Date(),
    trainerIds: [],
    gender: null,
    weeklyWorkouts: 0,
    isActive: true,
    fitnessGoal: '',
    hasAgreedToWeightIn: false,
    preferences: {
      hasAgreedToWeightIn: false,
      firstTimeWeighIn: true,
      hasSeenLibraryWelcomeModal: false,
      hasSeenTrackingWelcomeModal: false,
      hasSeenProgrammedWelcomeModal: false,
      initialPreferences: { ...initialPreferences },
      signUp: { ...signUpPreferences, version: '1.0.104' },
    },
    targetWeight: 0,
    weighIn: [],
    height: 0,
    gymIds: [],
    groupIds: [],
    hasAgreedToTerms: false,
    leadStatus: 'awaiting',
    ...memberOverrides,
  };
};

export const getUniqueMuscleGroupsNames = (
  exercises: Exercise[],
  muscles: Record<string, Muscle>,
): string[] => {
  const muscleGroupsNames = exercises
    .reduce(
      (acc, { primaryMuscleGroupIds }) => [
        ...acc,
        ...primaryMuscleGroupIds.map((id) => muscles[id]?.name),
      ],
      [] as (string | undefined)[],
    )
    .filter(Boolean) as string[];
  return [...new Set(muscleGroupsNames)];
};

export const strToOptionObj = (value: string, placeholder?: string) => ({
  label: value || placeholder || '',
  value,
});

// ------------------------- CALENDAR HELPERS -------------------------
// Build calendar structures based on Sunday-start weeks without
// disturbing the global constants ordering used elsewhere.

/** Returns a new Date set to the start of week (Sunday) for the provided date. */
export function startOfWeekSunday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = day; // days since Sunday
  const sunday = new Date(d);
  sunday.setHours(0, 0, 0, 0);
  sunday.setDate(d.getDate() - diff);
  return sunday;
}

/** Returns a new Date offset by the provided number of days. */
export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** Returns a new Date offset by the provided number of weeks. */
export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

/**
 * Build an array of weeks (each week is an array of 7 dates) starting from the
 * calendar week that contains the given start date. Weeks start on Sunday.
 */
export function buildWeeksFromStart(
  startDate: Date,
  weeksCount: number,
): Date[][] {
  const firstWeekSunday = startOfWeekSunday(startDate);
  const result: Date[][] = [];
  for (let w = 0; w < weeksCount; w++) {
    const weekStart = addWeeks(firstWeekSunday, w);
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(addDays(weekStart, i));
    }
    result.push(week);
  }
  return result;
}
